/** Student welcome-meeting detail tab. */
import * as React from 'react';
import { UserCheck, Eye, FileEdit } from 'lucide-react';
import { useQuery } from '@tanstack/react-query';
import { LabelBadge } from '@/components/common/LabelBadge';
import { DetailsCard } from '@/components/common/DetailsCard';
import { DetailField } from '@/components/common/details/DetailField';
import { Button } from '@/components/forms/Button';
import { useMarkStudentWelcomeMeetingParticipation } from '@/hooks/useUsers';
import { useNavigateToRemoteReport } from '@/components/sessions/remote/session-table-utils';
import { useAuthStore } from '@/stores/authStore';
import { getSession } from '@/services/remote/sessions.service';
import { WELCOME_MEETING_PARTICIPATION_CONFIG } from '@/components/students/student-configs';
import { formatDate } from '@/components/students/remote/student-table-utils';

/** Props for {@link StudentWelcomeMeetingTab}. */
interface StudentWelcomeMeetingTabProps {
  userId: string;
  sections: Record<string, unknown>;
  onEditSection?: (stepKey: string) => void;
}

/** Student welcome-meeting detail tab. */
export const StudentWelcomeMeetingTab: React.FC<StudentWelcomeMeetingTabProps> = ({
  userId,
  sections,
  onEditSection,
}) => {
  const { user } = useAuthStore();
  const { navigateToReport, isLoading: isLoadingReportNav } = useNavigateToRemoteReport();
  const welcomeMeetingMutation = useMarkStudentWelcomeMeetingParticipation();

  const welcomeMeeting = sections['welcome_meeting'] as any;
  const welcomeSessionId = welcomeMeeting?.sessionId;

  const { data: welcomeSession, isLoading: isLoadingWelcomeSession } = useQuery({
    queryKey: ['remote-session', welcomeSessionId],
    queryFn: () => getSession(welcomeSessionId!),
    enabled: !!welcomeSessionId,
  });

  const handleMarkWelcomeMeeting = () => {
    if (!user?.companyId) return;
    if (welcomeMeeting?.hasParticipated === true) return;
    welcomeMeetingMutation.mutate({
      userId,
      companyId: user.companyId,
      participated: true,
    });
  };

  return (
    <div className="details-view-grid mt-6">
      <DetailsCard icon={UserCheck} title="Réunion d'accueil" colorName="color-2" colSpan={2}>
        {!welcomeMeeting ? (
          <p className="text-sm text-muted-foreground">Aucune donnée de réunion d'accueil.</p>
        ) : (
          (() => {
            const markedByName =
              welcomeMeeting.markedBy?.userId === userId
                ? "S'est marqué lui-même"
                : `${welcomeMeeting.markedBy?.firstName || ''} ${welcomeMeeting.markedBy?.lastName || ''}`.trim() ||
                  '—';
            const participation =
              WELCOME_MEETING_PARTICIPATION_CONFIG[
                welcomeMeeting.hasParticipated ? 'done' : 'pending'
              ];
            return (
              <div className="space-y-3">
                <DetailField
                  label="Participation"
                  value={
                    <LabelBadge
                      label={participation.label}
                      icon={participation.icon}
                      variant={participation.badgeVariant}
                      color={(participation as { badgeColor?: string }).badgeColor}
                      size="sm"
                    />
                  }
                />
                {welcomeMeeting.hasParticipated && welcomeMeeting.markedBy ? (
                  <DetailField label="Marqué par" value={markedByName} />
                ) : (
                  <>
                    {welcomeMeeting.hasParticipated && welcomeMeeting.participatedAt && (
                      <DetailField
                        label="Date de participation"
                        value={formatDate(welcomeMeeting.participatedAt)}
                      />
                    )}
                  </>
                )}
              </div>
            );
          })()
        )}
      </DetailsCard>

      {onEditSection && (
        <div className="section-card-full flex gap-3">
          {(() => {
            const hasParticipated = welcomeMeeting?.hasParticipated === true;
            if (hasParticipated) return null;
            return (
              <Button
                size="sm"
                variant="outline"
                icon={UserCheck}
                loading={welcomeMeetingMutation.isPending}
                onClick={handleMarkWelcomeMeeting}
              >
                Marquer comme participant
              </Button>
            );
          })()}

          {welcomeSessionId &&
            welcomeSession?.status === 'COMPLETED' &&
            (() => {
              const reportValidated = ['valide', 'complet'].includes(
                (welcomeSession as any)?.compteRenduStatus ?? '',
              );
              return (
                <Button
                  size="sm"
                  variant="outline"
                  icon={reportValidated ? Eye : FileEdit}
                  loading={isLoadingWelcomeSession || isLoadingReportNav}
                  onClick={() => {
                    void navigateToReport(
                      welcomeSessionId,
                      'welcome_meeting',
                      (welcomeSession as { compteRenduId?: string | null }).compteRenduId,
                    );
                  }}
                >
                  {reportValidated ? 'Voir le compte rendu' : 'Saisir / modifier le compte rendu'}
                </Button>
              );
            })()}
        </div>
      )}
    </div>
  );
};
